/**
 * Standardized TanStack Query key factories for homy-web.
 * Enforces hierarchical scoping: all -> lists -> list(filters) -> details -> detail(id)
 */

export const queryKeys = {
  properties: {
    all: ["properties"],
    lists: () => [...queryKeys.properties.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.properties.lists(), { filters }] : queryKeys.properties.lists(),
    details: () => [...queryKeys.properties.all, "detail"],
    detail: (id) => [...queryKeys.properties.details(), id],
    smartLocks: (propertyId) => [...queryKeys.properties.detail(propertyId), "smart-locks"],
    smartLock: (propertyId, lockId) => [
      ...queryKeys.properties.smartLocks(propertyId),
      lockId,
    ],
  },

  maintenance: {
    all: ["maintenance"],
    lists: () => [...queryKeys.maintenance.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.maintenance.lists(), { filters }] : queryKeys.maintenance.lists(),
    details: () => [...queryKeys.maintenance.all, "detail"],
    detail: (id) => [...queryKeys.maintenance.details(), id],
    requests: (filters) =>
      filters
        ? [...queryKeys.maintenance.all, "requests", { filters }]
        : [...queryKeys.maintenance.all, "requests"],
    projects: (filters) =>
      filters
        ? [...queryKeys.maintenance.all, "projects", { filters }]
        : [...queryKeys.maintenance.all, "projects"],
    project: (id) => [...queryKeys.maintenance.all, "projects", id],
    vendors: (filters) =>
      filters
        ? [...queryKeys.maintenance.all, "vendors", { filters }]
        : [...queryKeys.maintenance.all, "vendors"],
    statements: (filters) =>
      filters
        ? [...queryKeys.maintenance.all, "statements", { filters }]
        : [...queryKeys.maintenance.all, "statements"],
    quote: (id) => [...queryKeys.maintenance.all, "quote", id],
  },

  tasks: {
    all: ["tasks"],
    lists: () => [...queryKeys.tasks.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.tasks.lists(), { filters }] : queryKeys.tasks.lists(),
    details: () => [...queryKeys.tasks.all, "detail"],
    detail: (id) => [...queryKeys.tasks.details(), id],
  },

  users: {
    all: ["users"],
    lists: () => [...queryKeys.users.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.users.lists(), { filters }] : queryKeys.users.lists(),
    details: () => [...queryKeys.users.all, "detail"],
    detail: (id) => [...queryKeys.users.details(), id],
    profile: () => [...queryKeys.users.all, "profile"],
    current: () => [...queryKeys.users.all, "current"],
    teamMembers: () => [...queryKeys.users.all, "team-members"],
  },

  inbox: {
    all: ["inbox"],
    lists: () => [...queryKeys.inbox.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.inbox.lists(), { filters }] : queryKeys.inbox.lists(),
    unreadCount: () => [...queryKeys.inbox.all, "unread-count"],
    notifications: () => [...queryKeys.inbox.all, "notifications"],
  },

  organizations: {
    all: ["organizations"],
    lists: () => [...queryKeys.organizations.all, "list"],
    list: (filters) =>
      filters
        ? [...queryKeys.organizations.lists(), { filters }]
        : queryKeys.organizations.lists(),
    details: () => [...queryKeys.organizations.all, "detail"],
    detail: (id) => [...queryKeys.organizations.details(), id],
    current: () => [...queryKeys.organizations.all, "current"],
    available: () => [...queryKeys.organizations.all, "available"],
  },

  rentals: {
    all: ["rentals"],
    lists: () => [...queryKeys.rentals.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.rentals.lists(), { filters }] : queryKeys.rentals.lists(),
    details: () => [...queryKeys.rentals.all, "detail"],
    detail: (id) => [...queryKeys.rentals.details(), id],
    contracts: (filters) =>
      filters
        ? [...queryKeys.rentals.all, "contracts", { filters }]
        : [...queryKeys.rentals.all, "contracts"],
    contract: (id) => [...queryKeys.rentals.all, "contracts", id],
    payments: (filters) =>
      filters
        ? [...queryKeys.rentals.all, "payments", { filters }]
        : [...queryKeys.rentals.all, "payments"],
  },

  calendar: {
    all: ["calendar"],
    schedules: (filters) =>
      filters
        ? [...queryKeys.calendar.all, "schedules", { filters }]
        : [...queryKeys.calendar.all, "schedules"],
    schedule: (id) => [...queryKeys.calendar.all, "schedules", id],
    eventTypes: () => [...queryKeys.calendar.all, "event-types"],
    eventType: (id) => [...queryKeys.calendar.all, "event-types", id],
    tours: (filters) =>
      filters
        ? [...queryKeys.calendar.all, "tours", { filters }]
        : [...queryKeys.calendar.all, "tours"],
    tour: (id) => [...queryKeys.calendar.all, "tours", id],
  },

  comments: {
    all: ["comments"],
    lists: () => [...queryKeys.comments.all, "list"],
    list: (entityType, entityId) => [
      ...queryKeys.comments.lists(),
      entityType,
      entityId,
    ],
    detail: (id) => [...queryKeys.comments.all, "detail", id],
  },

  approvals: {
    all: ["approvals"],
    lists: () => [...queryKeys.approvals.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.approvals.lists(), { filters }] : queryKeys.approvals.lists(),
    pending: () => [...queryKeys.approvals.all, "pending"],
    entity: (entityType, entityId) => [
      ...queryKeys.approvals.all,
      entityType,
      entityId,
    ],
  },

  trust: {
    all: ["trustProfile"],
    me: () => [...queryKeys.trust.all, "me"],
    user: (id) => [...queryKeys.trust.all, id],
  },

  issues: {
    all: ["issues"],
    lists: () => [...queryKeys.issues.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.issues.lists(), { filters }] : queryKeys.issues.lists(),
    details: () => [...queryKeys.issues.all, "detail"],
    detail: (id) => [...queryKeys.issues.details(), id],
  },

  databases: {
    all: ["databases"],
    lists: () => [...queryKeys.databases.all, "list"],
    list: (filters) =>
      filters ? [...queryKeys.databases.lists(), { filters }] : queryKeys.databases.lists(),
    details: () => [...queryKeys.databases.all, "detail"],
    detail: (id) => [...queryKeys.databases.details(), id],
    table: (dbId, tableId) => [
      ...queryKeys.databases.detail(dbId),
      "tables",
      tableId,
    ],
  },

  homysign: {
    all: ["homysign"],
    templates: (filters) =>
      filters
        ? [...queryKeys.homysign.all, "templates", { filters }]
        : [...queryKeys.homysign.all, "templates"],
    template: (id) => [...queryKeys.homysign.all, "templates", id],
    documents: (filters) =>
      filters
        ? [...queryKeys.homysign.all, "documents", { filters }]
        : [...queryKeys.homysign.all, "documents"],
    document: (id) => [...queryKeys.homysign.all, "documents", id],
  },
};

export default queryKeys;
